import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Sparkles, AlertCircle, MessageCircle } from "lucide-react";

export default function EvaDigestDetail() {
  const { id } = useParams();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("weekly_digest"));
  const [doc, setDoc] = useState(undefined);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/eva/digests/${id}`); setDoc(data); }
    catch { setDoc(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (doc === undefined) return <Spinner label={t("common.loading")} />;

  const analysis = doc?.analysis || {};
  const summary = analysis.summary || "";
  const patterns = Array.isArray(analysis.patterns) ? analysis.patterns : [];
  const nextStep = analysis.next_step || analysis.suggestion || "";
  const mood = analysis.mood || "";

  return (
    <div className="space-y-5" data-testid="eva-digest-detail">
      <button onClick={() => navigate("/app/weva/digests")} data-testid="digest-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("weva_digests_title")}
      </button>

      {!doc ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("report_not_found")}</p>
          <Button variant="outline" onClick={load} className="rounded-full">{t("retry")}</Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3.5 w-3.5" /> {t("weva")} · {shortDate(doc.created_at)}{mood ? ` · ${mood}` : ""}
          </div>

          {summary ? (
            <p className="whitespace-pre-wrap text-base leading-relaxed text-foreground">{summary}</p>
          ) : (
            <p className="text-sm italic text-muted-foreground">{t("weva_didn_t_leave_a_summary_for_this")}</p>
          )}

          {patterns.length > 0 && (
            <div className="space-y-2 rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-base text-foreground">{t("what_weva_noticed")}</h2>
              {patterns.map((p, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <p className="text-sm leading-relaxed text-foreground">{p}</p>
                </div>
              ))}
            </div>
          )}

          {nextStep ? (
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5">
              <h2 className="font-display text-base text-primary">{t("a_gentle_next_step")}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground">{nextStep}</p>
            </div>
          ) : null}

          <Button onClick={() => navigate("/app/weva/chat")} data-testid="open-eva-chat" className="w-full gap-2 rounded-full">
            <MessageCircle className="h-4 w-4" /> {t("talk_with_weva_about_this")}
          </Button>
        </>
      )}
    </div>
  );
}
